import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BaseUrl, ImgUrl } from "../common/BaseUrl";
import Modal from "react-modal";
import axios from "axios";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProfileData,
  updateProfile,
} from "../../store/Profile/profileSlice";
import Swal from "sweetalert2";
import { FiEdit2, FiSave, FiUser, FiMail, FiPhone, FiHome, FiBriefcase, FiCalendar, FiClock, FiDollarSign, FiAward } from "react-icons/fi";

Modal.setAppElement("#root");

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile.profile);
  const empId = localStorage.getItem("_id");

  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    address: "",
    city: "",
    lane: "",
    stateId: "",
    zip: "",
    jobTitle: "",
    department: "",
    employeeId: "",
    hireDate: "",
    empStatus: "",
    payPeriod: "",
    rate: "",
    timeOff: "",
    availabilityPreference: "",
    firstName: "",
    lastName: "",
    middleName: "",
    ssnNo: "",
  });

  useEffect(() => {
    if (empId) {
      dispatch(fetchProfileData(empId));
      empDataGetById();
    }
  }, [empId, refresh]);

  useEffect(() => {
    if (profile) {
      setFormData({
        ...profile,
        hireDate: profile.hireDate ? moment(profile.hireDate).format("YYYY-MM-DD") : "",
      });
    }
  }, [profile]);

  const handleOnChangeImage = async (evt) => {
    setPreview(URL.createObjectURL(evt.target.files[0]));
    const formData = new FormData();
    formData.append("id", empId);
    formData.append("profile", evt.target.files[0]);

    try {
      await axios.post(`${BaseUrl}/emp/profile-update`, formData);
      setRefresh(!refresh);
    } catch (error) {
      console.error("Image upload error:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updateData = { ...formData };
    dispatch(updateProfile({ empId, updateData }))
      .then((response) => {
        if (response.meta.requestStatus === "fulfilled") {
          showSuccessAlert("success", "Profile updated successfully");
        }
      })
      .catch((error) => {
        console.error("Error updating profile:", error);
        showSuccessAlert("error", "Failed to update profile");
      });
  };

  const showSuccessAlert = (icon, msg) => {
    Swal.fire({
      icon: icon,
      title: icon === "success" ? "Success!" : "Error!",
      text: msg,
      confirmButtonText: "OK",
      confirmButtonColor: "#3085d6",
    });
  };

  const empDataGetById = async () => {
    try {
      const response = await fetch(`${BaseUrl}/emp/get-by-id/${empId}`);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setData(data.result);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 p-4 rounded-lg shadow-md mb-6">
          <h1 className="text-2xl font-bold text-white">My Profile</h1>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Profile Image Section */}
          <div className="bg-gray-50 p-6 flex flex-col md:flex-row items-center border-b">
            <div className="relative group mb-4 md:mb-0">
              <img
                className="rounded-full w-32 h-32 border-4 border-white shadow-md object-cover"
                src={preview ? preview : `${ImgUrl}/empProfile/${data?.profile}`}
                alt="Profile"
              />
              <label 
                htmlFor="image-profile"
                className="absolute bottom-0 right-0 bg-green-500 text-white p-2 rounded-full cursor-pointer hover:bg-green-600 transition-all"
              >
                <FiEdit2 size={16} />
                <input
                  type="file"
                  id="image-profile"
                  className="hidden"
                  onChange={handleOnChangeImage}
                />
              </label>
            </div>
            
            <div className="md:ml-6 text-center md:text-left">
              <h2 className="text-2xl font-bold text-gray-800">
                {formData?.firstName} {formData?.lastName}
              </h2>
              <p className="text-gray-600">{formData?.jobTitle || "Employee"}</p>
              <p className="text-gray-600">{formData?.department || "Department"}</p>
              <p className="text-sm text-gray-500 mt-2">
                Employee ID: {`EMP${formData?.empId}`}
              </p>
            </div>
          </div>

          {/* Profile Form */}
          <div className="p-6">
            <form onSubmit={handleSubmit}>
              {/* Personal Information Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <FiUser className="text-green-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      value={formData?.firstName}
                      name="firstName"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Middle Name (optional)</label>
                    <input
                      value={formData?.middleName}
                      name="middleName"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      value={formData?.lastName}
                      name="lastName"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">SSN Number</label>
                    <input
                      type="text"
                      maxLength="9"
                      value={formData?.ssnNo}
                      name="ssnNo"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 9) {
                          handleInputChange({ target: { name: "ssnNo", value } });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <FiMail className="text-green-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <div className="relative">
                      <input
                        value={formData?.mobile}
                        name="mobile"
                        placeholder="(847) 825-5586"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      value={formData?.email}
                      name="email"
                      className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                      disabled
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <input
                      value={formData?.address}
                      name="address"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* Employment Details Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <FiBriefcase className="text-green-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800">Employment Details</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Job Title</label>
                    <input
                      value={formData?.jobTitle}
                      name="jobTitle"
                      placeholder="Manager"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Department</label>
                    <input
                      value={formData?.department}
                      name="department"
                      placeholder="Food Department"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Hire Date</label>
                    <input
                      value={formData.hireDate}
                      name="hireDate"
                      type="date"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={formData?.empStatus}
                      onChange={handleInputChange}
                      name="empStatus"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="active">Active</option>
                      <option value="terminated">Terminated</option>
                      <option value="leave">Leave</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Availability Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <FiClock className="text-green-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800">Availability</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Availability Preferences</label>
                    <input
                      value={formData?.availabilityPreference}
                      name="availabilityPreference"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Time-Off Requests</label>
                    <input
                      value={formData?.timeOff}
                      name="timeOff"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Shift Preference</label>
                    <input
                      name="shiftPreferences"
                      value={data?.shift?.length ? data?.shift[0].name : 'Not Assigned'}
                      className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                      disabled
                    />
                  </div>
                </div>
              </div>

              {/* Skills Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <FiAward className="text-green-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800">Skills & Certifications</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Skills</label>
                    <input
                      name="skills"
                      value={formData?.skills}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Certifications</label>
                    <input
                      name="certifications"
                      value={formData?.certifications}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* Compensation Section */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <FiDollarSign className="text-green-500 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-800">Compensation</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Hourly Wage/Salary</label>
                    <input
                      value={formData?.rate}
                      name="rate"
                      placeholder="10$"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Overtime Rate</label>
                    <input
                      disabled
                      value={formData?.overTimeRate}
                      placeholder=""
                      className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Pay Period</label>
                    <select
                      value={formData?.payPeriod}
                      name="payPeriod"
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="daily">Daily</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end mt-6">
                <button
                  type="submit"
                  className="flex items-center bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-md transition-colors"
                >
                  <FiSave className="mr-2" />
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;